import opentype from "opentype.js"
import { error } from "./logger"
import ClientData from "@/common/client_data"
import env from "@/common/env"
import Util from "@/common/util"

declare global {
  interface Window {
    webkitURL?: URL
  }
}

export interface FontVariant {
  path: string
  filename: string
  basename: string
  type: string
}

interface FontVariants {
  [index: string]: FontVariant | undefined
  normal: FontVariant
  bold?: FontVariant
  italic?: FontVariant
  bold_italic?: FontVariant
}

interface Fontmap {
  [index: string]: FontVariants
}

export interface Fontdata {
  filemap: Fontmap
  disabled: string[]
  sets: {
    [index: string]: string[]
    all: string[]
    custom: string[]
    cutter: string[]
    limited: string[]
  }
}

interface FontVariantsCache {
  [index: string]: opentype.Font | null | undefined
  normal?: opentype.Font
  bold?: opentype.Font
  italic?: opentype.Font
  bold_italic?: opentype.Font
}

interface FontCache {
  [index: string]: FontVariantsCache
}

let fontdataCopy: Fontdata = {
  filemap: {},
  disabled: [],
  sets: {
    all: [],
    custom: [],
    cutter: [],
    limited: [],
  },
}
const fontCache: FontCache = {}
const underlineDisabled: string[] = ["braille (se 6p)"]
const disabledFonts = ClientData.fontdata?.disabled.map((font: string) => font.toLowerCase()) || []
const allowedFonts: string[] = []

const Fonts = {
  /**
   * Returns a promises that fetches all variants of a font as ArrayBuffers and inserts them in the Opentype cache
   * and page webfonts.
   */
  loadFont(name: string, fontdata: Fontdata) {
    name = name.toLowerCase()

    const variants = fontdata.filemap[name]

    if (env.isJSDOM) {
      for (const key in variants) {
        if (Object.prototype.hasOwnProperty.call(variants, key)) {
          const element = variants[key]

          if (element) {
            if (fontCache[name] && fontCache[name]?.[key]) {
              continue
            }

            const customFonts = fontdata.sets.custom?.map(font => font.toLowerCase()) || []

            const fontpath = customFonts.includes(name)
              ? element.path
              : process.cwd() + "/app/assets/fonts/" + element.path

            const font = opentype.loadSync(fontpath)
            fontCache[name] = fontCache[name] || {}
            fontCache[name][key] = font
          }
        }
      }

      return Promise.all([])
    }

    const promises: Array<Promise<void>> = []

    for (const key in variants) {
      if (Object.prototype.hasOwnProperty.call(variants, key)) {
        const element = variants[key]

        if (element) {
          const promise = new Promise<void>((resolve, reject) => {
            if (fontCache[name] && fontCache[name]?.[key]) {
              return resolve()
            }

            opentype.load(element.path, undefined, { lowMemory: true }).then(
              font => {
                fontCache[name] ||= {}
                fontCache[name][key] = font
                resolve()
              },
              err => {
                error(`failed loading variant ${key} for font ${element.basename}:`)
                error(err)
                reject(err)
              }
            )
          })

          promises.push(promise)
        }
      }
    }

    return Promise.all(promises)
  },
  /**
   * Preload all fonts
   */
  loadFonts(fontdata: Fontdata) {
    if (Object.keys(fontdataCopy.filemap).length === 0) {
      fontdataCopy = fontdata
    }

    for (const name in fontdata?.filemap) {
      if (Object.prototype.hasOwnProperty.call(fontdata?.filemap, name)) {
        if (name === "braille (se 6p)") continue

        Fonts.loadFont(name, fontdata).then(null, err => {
          error(err)
        })
      }
    }
  },
  getOpentypeFont(name: string, variant: string) {
    name = name.toLowerCase()

    if (!fontCache[name]) {
      return null
    }

    return fontCache[name]?.[variant]
  },
  allowedStyles(name: string, fontdata?: Fontdata) {
    const variant = fontdata?.filemap[name.toLowerCase()] || fontdataCopy?.filemap[name.toLowerCase()]

    if (!variant) {
      return { bold: false, italic: false, boldItalic: false }
    }

    return {
      bold: !Util.objEmpty(variant.bold),
      italic: !Util.objEmpty(variant.italic),
      boldItalic: !Util.objEmpty(variant.bold_italic),
    }
  },
  disableUnderline(fontName: string, signType: string) {
    return underlineDisabled.includes(fontName.toLowerCase()) || signType == "contour_cut"
  },
  disabledFonts(signType: string, material?: string, fontdata?: Fontdata) {
    if (env.isJSDOM) {
      return []
    }

    let fonts = fontdata?.disabled.map((font: string) => font.toLowerCase()) || disabledFonts

    if (!env.isJSDOM && !ClientData.admin) {
      fonts = [...fonts, "Code 128", "Arial Narrow", "Free 3 Of 9 Regular", "Free 3 Of 9 Extended Regular"]
    }

    if (signType == "brass_sign") {
      fonts = [...fonts, "Exmouth", "Old Script", "GeosansLight", "Existence Light", "Dancing Script"]
    }

    if (signType == "color_badge") {
      fonts = [...fonts, "Exmouth", "Existence Light"]
    }

    if (["plastic_sign", "name_sign"].includes(signType) || (signType === "label_plates" && material === "regular")) {
      fonts = [...fonts, "Caesar"]
    }

    if (signType === "vinyl_text") {
      fonts = [...fonts, "Amatic SC", "Times New Roman", "Playfair Display", "Abril Fatface", "Cormorant"]
    }

    if (env.locale == "cs-CZ") {
      fonts = [
        ...fonts,
        "Amerika Sans",
        "Apple Garamond",
        "Arial Rounded Mt Bold",
        "BorisBlackBloxx",
        "Caesar",
        "Clarendon",
        "Daniel",
        "Day roman",
        "Existence light",
        "Exmouth",
        "Fiolex girls",
        "GeosansLight",
        "Graduate",
        "Handlee",
        "Lato",
        "Lobster Two",
        "Old London",
        "Old script",
        "Stardos stencil",
        "Street corner",
        "TratexVit",
        "Traveling _Typewriter",
        "Walkway bold",
        "Wiener norm",
      ]
    }

    return fonts.map(font => font.toLowerCase()).filter(name => !allowedFonts.includes(name))
  },
  allowFont(name: string) {
    allowedFonts.push(name)
  },
  fontExists(name: string) {
    return !!fontdataCopy?.filemap[name.toLowerCase()]
  },
  enabledFonts(signType: string, fontdata: Fontdata, fontSet: string, material?: string) {
    const disabled = this.disabledFonts(signType, material, fontdata).map((font: string) => font.toLowerCase())

    return fontdata.sets[fontSet]?.filter(f => !disabled.includes(f.toLowerCase())) || []
  },
}

export default Fonts
